import { apiClient, apiClientPaginated } from '@/lib/api-client';

// ─── Response types ──────────────────────────────────────────────────────────

export interface DaySummary {
  revenue: number;
  bill_count: number;
  avg_bill_value: number;
  gross_profit?: number;
  margin_pct?: number;
  payment_breakdown?: Record<string, number>;
}

export interface OnboardingFlags {
  has_location: boolean;
  has_products: boolean;
  has_team: boolean;
  first_sale_completed: boolean;
}

export interface OrderTypeBucket {
  bill_count: number;
  revenue: number;
}

export interface SalesAnomaly {
  rule: 'WEEKDAY_AVERAGE';
  threshold_pct: number;
  weeks_compared: number;
  as_of: string;
  weekday_average: number;
  change_pct: number | null;
  is_anomaly: boolean;
}

export interface DashboardSummary {
  today: DaySummary & { payment_breakdown: Record<string, number> };
  yesterday: DaySummary;
  growth_pct: number;
  growth_direction: 'UP' | 'DOWN';
  /** Today so far against the same weekday's average for the same period (FR-DASH-004/009). */
  anomaly?: SalesAnomaly;
  active_bills: number;
  pending_approvals: number;
  low_stock_count: number;
  onboarding: OnboardingFlags;
  order_type_breakdown: Record<'DINE_IN' | 'TAKEAWAY' | 'DELIVERY', OrderTypeBucket>;
  customer_split: { new_count: number; returning_count: number };
  updated_at: string;
}

export interface HourlySales {
  hour: number;
  revenue: number;
  bill_count: number;
}

export interface SalesWaveResponse {
  today: HourlySales[];
  comparison: HourlySales[];
  /** Primary date when days=1; end of the range when days>1. */
  date?: string;
  comparison_date: string;
  /** 1 for single-day mode, >=2 for averaged window mode. */
  days?: number;
  range?: { start: string; end: string };
  comparison_range?: { start: string; end: string };
  peak_hour_today: number | null;
  peak_hour_comparison: number | null;
}

export interface DrillDownDetail {
  product_id: string;
  product_name: string;
  quantity_sold: number;
  revenue: number;
  margin_pct: number;
}

export interface DrillDownResponse {
  data: {
    dimension: string;
    value: string;
    summary: {
      revenue: number;
      bill_count: number;
      avg_bill_value: number;
      margin_pct: number;
    };
    details: DrillDownDetail[];
  };
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface TopProductItem {
  rank: number;
  product_id: string;
  product_name: string;
  category?: string;
  quantity_sold: number;
  revenue: number;
}

export interface TopProductsResponse {
  items: TopProductItem[];
}

export interface BillListItem {
  id: string;
  bill_number: string;
  status: string;
  grand_total: number;
  payment_method: string;
  customer_name?: string;
  created_at: string;
  location_name?: string;
  item_count: number;
}

export interface BillListResponse {
  data: BillListItem[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const dashboardService = {
  getSummary(locationId?: string) {
    const params = locationId ? `?location_id=${locationId}` : '';
    return apiClient<DashboardSummary>(`/dashboard/summary${params}`);
  },

  getSalesWave(params: {
    locationId?: string;
    compareDate?: string;
    date?: string;
    days?: number;
  } = {}) {
    const query = new URLSearchParams();
    if (params.locationId) query.set('location_id', params.locationId);
    if (params.compareDate) query.set('compare_date', params.compareDate);
    if (params.date) query.set('date', params.date);
    if (params.days) query.set('days', String(params.days));
    const qs = query.toString();
    return apiClient<SalesWaveResponse>(`/dashboard/sales-wave${qs ? `?${qs}` : ''}`);
  },

  /** `date`: the day to rank, YYYY-MM-DD on the store's clock; the UTC date when omitted. */
  getTopProducts(locationId?: string, limit = 5, date?: string) {
    const query = new URLSearchParams();
    const today = date ?? new Date().toISOString().slice(0, 10);
    query.set('from', today);
    query.set('to', today);
    query.set('sort', 'revenue');
    query.set('limit', String(limit));
    if (locationId) query.set('location_id', locationId);
    return apiClient<TopProductsResponse>(`/bills/reports/top-products?${query.toString()}`);
  },

  getRecentBills(locationId?: string, limit = 5) {
    const query = new URLSearchParams();
    query.set('limit', String(limit));
    query.set('page', '1');
    // No sort param: GET /bills rejects unknown params and already lists newest first.
    if (locationId) query.set('location_id', locationId);
    return apiClientPaginated<BillListItem[]>(`/bills?${query.toString()}`);
  },

  getDrillDown(params: {
    dimension: 'CATEGORY' | 'TIME_SEGMENT' | 'PAYMENT_MODE' | 'PRODUCT';
    value: string;
    locationId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }) {
    const query = new URLSearchParams();
    query.set('dimension', params.dimension);
    query.set('value', params.value);
    if (params.locationId) query.set('location_id', params.locationId);
    if (params.startDate) query.set('start_date', params.startDate);
    if (params.endDate) query.set('end_date', params.endDate);
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    // The envelope carries the drill-down in `data` and the product-row paging in `meta`.
    return apiClientPaginated<DrillDownResponse['data']>(`/dashboard/drill-down?${query.toString()}`);
  },

  getTopCategories(params: {
    locationId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
  } = {}) {
    const query = new URLSearchParams();
    if (params.locationId) query.set('location_id', params.locationId);
    if (params.startDate) query.set('start_date', params.startDate);
    if (params.endDate) query.set('end_date', params.endDate);
    if (params.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return apiClient<TopCategoriesResponse>(
      `/dashboard/top-categories${qs ? `?${qs}` : ''}`,
    );
  },

  getSalesHeatmap(params: { locationId?: string; days?: number } = {}) {
    const query = new URLSearchParams();
    if (params.locationId) query.set('location_id', params.locationId);
    if (params.days) query.set('days', String(params.days));
    const qs = query.toString();
    return apiClient<SalesHeatmapResponse>(
      `/dashboard/sales-heatmap${qs ? `?${qs}` : ''}`,
    );
  },
};

// ─── Top categories + heatmap response types ─────────────────────────────────

export interface TopCategoryItem {
  key: string;
  name: string;
  revenue: number;
  quantity_sold: number;
  bill_count: number;
  share_pct: number;
}

export interface TopCategoriesResponse {
  range: { start: string; end: string };
  total_revenue: number;
  categories: TopCategoryItem[];
}

export interface SalesHeatmapCell {
  /** 0 = Sunday … 6 = Saturday, on the store's clock (T18). */
  day_of_week: number;
  /** 0-23, on the store's clock (T18). */
  hour: number;
  bill_count: number;
  revenue: number;
}

export interface SalesHeatmapResponse {
  range: { start: string; end: string; days: number };
  /** Maximum cell revenue, useful for choosing a colour scale. */
  max_revenue: number;
  /** Maximum cell bill count. */
  max_bill_count: number;
  cells: SalesHeatmapCell[];
}
